import { Injectable, inject, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { TENANTS_INFO } from '@fitness-force/state';
import type { TENANTSINTERFACE, TenantSettingByKey } from '@fitness-force/models';
import { Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { AdvancedSettingService } from '../admin/advanced-setting.service';

export const DISPLAY_UPFRONT_INSTEAD_OF_DEPOSIT_KEY =
  'display_upfront_instead_of_deposit';

const BUTTON_ADD_DEPOSIT_KEY = 'common.BUTTONS.BTN_ADD_DEPOSIT';
const BUTTON_ADD_DEBIT_UPFRONT_KEY = 'common.BUTTONS.BTN_ADD_DEBIT_UPFRONT';

const POS_MEMBERSHIP_KEY_MAP: Readonly<Record<string, string>> = {
  'POS.MEMBERSHIP_ADDON.DEPOSIT': 'POS.MEMBERSHIP_ADDON.DEBIT_UPFRONT_DEPOSIT',
  'POS.MEMBERSHIP_ADDON.OCCURENCE': 'POS.MEMBERSHIP_ADDON.DEBIT_UPFRONT_OCCURENCE',
  'POS.MEMBERSHIP_ADDON.LAST_OCCURENCE':
    'POS.MEMBERSHIP_ADDON.DEBIT_UPFRONT_LAST_OCCURENCE',
  'POS.MEMBERSHIP_ADDON.LAST_OCCURENCE_ONLY':
    'POS.MEMBERSHIP_ADDON.DEBIT_UPFRONT_LAST_OCCURENCE_ONLY',
};

@Injectable({
  providedIn: 'root',
})
export class DepositTerminologyService {
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly advanceSettingService = inject(AdvancedSettingService);

  private readonly useDebitUpfrontSignal = signal(false);

  readonly useDebitUpfront = this.useDebitUpfrontSignal.asReadonly();

  constructor() {
    this.store
      .select(TENANTS_INFO)
      .pipe(
        map((tenants: TENANTSINTERFACE) => tenants?.selected_tenant?.id ?? 0),
        distinctUntilChanged(),
        switchMap((tenantId: number) => {
          if (!tenantId || tenantId <= 0) {
            this.useDebitUpfrontSignal.set(false);
            return of(false);
          }
          return this.loadSetting(tenantId);
        }),
      )
      .subscribe((enabled) => {
        this.useDebitUpfrontSignal.set(enabled);
      });
  }

  setFromValue(value: string | boolean): void {
    this.useDebitUpfrontSignal.set(this.parseBooleanValue(value));
  }

  refresh(tenantId: number): void {
    if (!tenantId || tenantId <= 0) {
      this.useDebitUpfrontSignal.set(false);
      return;
    }
    this.loadSetting(tenantId).subscribe((enabled) => {
      this.useDebitUpfrontSignal.set(enabled);
    });
  }

  resolveKey(key: string): string {
    if (!key || !this.useDebitUpfrontSignal()) {
      return key;
    }

    if (key === BUTTON_ADD_DEPOSIT_KEY) {
      return BUTTON_ADD_DEBIT_UPFRONT_KEY;
    }

    const posMapped = POS_MEMBERSHIP_KEY_MAP[key];
    if (posMapped) {
      return posMapped;
    }

    if (key.startsWith('DEPOSIT.') && !key.startsWith('DEPOSIT.DEBIT_UPFRONT.')) {
      const suffix = key.slice('DEPOSIT.'.length);
      return `DEPOSIT.DEBIT_UPFRONT.${suffix}`;
    }

    return key;
  }

  resolveKeys(keys: string[]): string[] {
    return keys.map((key) => this.resolveKey(key));
  }

  /**
   * Formats deposit / debit-upfront bill line labels.
   * Matches existing convention: occurrence -1 shows "Last Occurrence"
   * (not "1st Last Occurrence"); -2/-3 show "2nd/3rd Last Occurrence".
   */
  formatOccurrenceLabel(depositOccurence: number, count: number): string {
    if (depositOccurence > 0) {
      return this.instant('POS.MEMBERSHIP_ADDON.OCCURENCE', {
        suffix: this.toOrdinalSuffix(depositOccurence),
        count,
      });
    }

    if (depositOccurence < 0) {
      const absValue = Math.abs(depositOccurence);
      if (absValue === 1) {
        return this.instant('POS.MEMBERSHIP_ADDON.LAST_OCCURENCE_ONLY', {
          count,
        });
      }
      return this.instant('POS.MEMBERSHIP_ADDON.LAST_OCCURENCE', {
        suffix: this.toOrdinalSuffix(absValue),
        count,
      });
    }

    return this.instant('POS.MEMBERSHIP_ADDON.DEPOSIT', { count });
  }

  instant(key: string, interpolateParams?: object): string {
    const resolved = this.resolveKey(key);
    const translated = this.translate.instant(resolved, interpolateParams);
    if (translated !== resolved) {
      return translated;
    }
    if (resolved !== key) {
      return this.translate.instant(key, interpolateParams);
    }
    return translated;
  }

  get(key: string, interpolateParams?: object): Observable<string>;
  get(key: string[], interpolateParams?: object): Observable<Record<string, string>>;
  get(
    key: string | string[],
    interpolateParams?: object,
  ): Observable<string | Record<string, string>> {
    if (Array.isArray(key)) {
      const resolvedKeys = this.resolveKeys(key);
      return this.translate.get(resolvedKeys, interpolateParams).pipe(
        map((translations: Record<string, string>) => {
          const result: Record<string, string> = {};
          key.forEach((originalKey, index) => {
            const resolved = resolvedKeys[index];
            const value = translations[resolved];
            if (value !== undefined && value !== resolved) {
              result[originalKey] = value;
            } else {
              result[originalKey] = this.translate.instant(
                originalKey,
                interpolateParams,
              );
            }
          });
          return result;
        }),
      );
    }

    const resolved = this.resolveKey(key);
    return this.translate.get(resolved, interpolateParams).pipe(
      map((translated: string) => {
        if (translated !== resolved) {
          return translated;
        }
        if (resolved !== key) {
          return this.translate.instant(key, interpolateParams);
        }
        return translated;
      }),
    );
  }

  private loadSetting(tenantId: number): Observable<boolean> {
    return this.advanceSettingService
      .getAdvanceSetting(tenantId, DISPLAY_UPFRONT_INSTEAD_OF_DEPOSIT_KEY)
      .pipe(
        map((setting: TenantSettingByKey) => this.parseBooleanValue(setting?.value)),
        catchError(() => of(false)),
      );
  }

  private parseBooleanValue(value: string | boolean | undefined | null): boolean {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'string') {
      return value.toLowerCase() === 'true';
    }
    return false;
  }

  private toOrdinalSuffix(value: number): string {
    const ordinal =
      value <= 3 ? (['st', 'nd', 'rd'][value - 1] ?? 'th') : 'th';
    return `${value}${ordinal}`;
  }
}
